import React from 'react';
import dayjs from 'dayjs';
import { useDebouncedCallback } from 'use-debounce';
import { Button, Card, Col, Space, Row } from 'antd';
import { Form, PRETTY_DATE_FORMAT } from '@owl-frontend/components';
import type { User } from '../../../api/interface';
import { Locale } from '../../../context/AppProvider';
import styles from '../ClaimsContainer.module.scss';
import type { ClaimsHookState } from '../useClaimsHook';

interface Props {
  users: User[];
  filters: ClaimsHookState['filters'];
  onFilterChange(data: { filters: ClaimsHookState['filters'] }): void;
  advancedFilter?: boolean;
  onClearFilter(): void;
}

const ClaimsFilterForm: Form.MFC<
  Pick<Props, 'users' | 'advancedFilter' | 'onClearFilter'>
> = ({
  users,
  advancedFilter,
  onClearFilter,
  // RFF
  handleSubmit,
  values,
  pristine,
}) => {
  const { messages } = React.useContext(Locale);

  const ownerOptions = React.useMemo(
    () =>
      users.map((u) => ({
        label: u.full_name,
        value: u.email,
        searchText: `${u.full_name} ${u.email}`,
      })),
    [users]
  );

  React.useEffect(() => {
    if (pristine) {
      return;
    }

    handleSubmit(values);
  }, [handleSubmit, values, pristine]);

  return (
    <form onSubmit={handleSubmit}>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Row>
          <Col span={24} className={styles.nameIdFilterRow}>
            <Form.InputManaged
              data-dd-privacy="allow"
              name="name_or_id"
              label={
                <span style={{ fontWeight: 'normal' }}>
                  {messages['claims.hub.filters.search.label']}
                </span>
              }
              placeholder={messages['claims.hub.filters.search.placeholder']}
              style={{ width: '100%', fontWeight: 'normal', marginBottom: 0 }}
            />
            <Button htmlType="button" onClick={onClearFilter}>
              {messages['claims.hub.filters.clear']}
            </Button>
          </Col>
        </Row>
        {advancedFilter && (
          <Row gutter={12}>
            <Col span={8}>
              <Form.MultiSelectAntdManaged
                data-dd-privacy="allow"
                name="owners"
                label={
                  <span style={{ fontWeight: 'normal' }}>
                    {messages['claims.hub.filters.reviewer.label']}
                  </span>
                }
                placeholder={
                  messages['claims.hub.filters.reviewer.placeholder']
                }
                options={ownerOptions}
                maxTagCount={'responsive'}
                filterOption={(input, option) =>
                  option?.searchText.toLowerCase().includes(input.toLowerCase())
                }
              />
            </Col>
            <Col span={8}>
              <Form.MultiSelectAntdManaged
                data-dd-privacy="allow"
                name="statuses"
                label={
                  <span style={{ fontWeight: 'normal' }}>
                    {messages['claims.hub.filters.status.label']}
                  </span>
                }
                placeholder={messages['claims.hub.filters.status.placeholder']}
                options={[
                  {
                    label: messages['claims.hub.filters.status.values.open'],
                    value: 'Open',
                  },
                  {
                    label: messages['claims.hub.filters.status.values.pending'],
                    value: 'Pending',
                  },
                  {
                    label: messages['claims.hub.filters.status.values.closed'],
                    value: 'Closed',
                  },
                ]}
              />
            </Col>
            <Col span={8}>
              <Form.DateRangePickerAntdManaged
                data-dd-privacy="allow"
                name="cid_date"
                label={
                  <span style={{ fontWeight: 'normal' }}>
                    {messages['claims.hub.filters.cid_date.label']}
                  </span>
                }
                format={(date) => `${dayjs(date).format(PRETTY_DATE_FORMAT)}`}
              />
            </Col>
          </Row>
        )}
      </Space>
    </form>
  );
};

const ClaimsFilterFormManaged = Form.manage(ClaimsFilterForm);

const ClaimsListFilterComponent: React.FC<Props> = ({
  users,
  filters,
  onFilterChange,
  advancedFilter,
  onClearFilter,
}) => {
  const onSubmit = useDebouncedCallback((values) => {
    onFilterChange({ filters: values });
  }, 500);

  return (
    <Card className={styles.filters}>
      <ClaimsFilterFormManaged
        users={users}
        initialValues={filters}
        onSubmit={onSubmit}
        advancedFilter={advancedFilter}
        onClearFilter={onClearFilter}
      />
    </Card>
  );
};

export default ClaimsListFilterComponent;
